import { SystemAttributeIds } from "@packages/core/organization/systemAttributeId.ts";
import type { EmployeeFieldType, PeriodType, RuleOperator, SegmentRule } from "#shared/segmentsTypes.ts";
import { parseRuleDate, parseRuleNumber } from "./ruleValues.ts";

const RuleConditions = ["is", "isExactly", "isNot", "isAnyOf", "isNoneOf", "isBefore", "isAfter", "isGreaterThan", "isLessThan", "isBetween", "isOlderThan", "isWithinTheLast", "occurredBetween", "occursInMoreThan", "occursInLessThan", "occursInBetween", "directlyTo", "upThrough"] as const;

export type RuleCondition = (typeof RuleConditions)[number];

export const REPORTING_MANAGER_SYSTEM_ATTRIBUTE_ID = SystemAttributeIds.ReportingManager;

type ValueArity = "single" | "multiple" | "range" | "period" | "periodRange";

export const CONDITIONS_BY_FIELD_TYPE: Record<EmployeeFieldType, readonly RuleCondition[]> = {
    option: ["is", "isNot", "isAnyOf", "isNoneOf"],
    date: ["isAfter", "isBefore", "isBetween", "isExactly", "isNot", "occurredBetween", "isWithinTheLast", "isOlderThan", "occursInBetween", "occursInLessThan", "occursInMoreThan"],
    number: ["is", "isNot", "isGreaterThan", "isLessThan", "isBetween"],
    person: ["directlyTo", "upThrough"],
    personReference: ["is", "isNot", "isAnyOf", "isNoneOf"]
};

const VALUE_ARITY: Record<RuleCondition, ValueArity> = {
    is: "single",
    isExactly: "single",
    isNot: "single",
    isAnyOf: "multiple",
    isNoneOf: "multiple",
    isBefore: "single",
    isAfter: "single",
    isGreaterThan: "single",
    isLessThan: "single",
    isBetween: "range",
    isOlderThan: "period",
    isWithinTheLast: "period",
    occurredBetween: "periodRange",
    occursInMoreThan: "period",
    occursInLessThan: "period",
    occursInBetween: "periodRange",
    directlyTo: "multiple",
    upThrough: "multiple"
};

export function valueArity(condition: RuleCondition) {
    return VALUE_ARITY[condition];
}

export function toRuleOperator(condition: RuleCondition, valueCount: number): RuleOperator {
    switch (condition) {
        case "is":
        case "isExactly":
            return "Equal";
        case "directlyTo":
            return "AnyOf";
        case "isNot":
            return "NotEqual";
        case "isAnyOf":
            return valueCount > 1 ? "AnyOf" : "Equal";
        case "isNoneOf":
            return valueCount > 1 ? "NoneOf" : "NotEqual";
        case "isBefore":
        case "isLessThan":
            return "LowerThan";
        case "isAfter":
        case "isGreaterThan":
            return "GreaterThan";
        case "isBetween":
            return "Between";
        case "isOlderThan":
            return "OccurredMoreThan";
        case "isWithinTheLast":
            return "OccurredLessThan";
        case "occurredBetween":
            return "OccurredBetween";
        case "occursInMoreThan":
            return "OccursInMoreThan";
        case "occursInLessThan":
            return "OccursInLessThan";
        case "occursInBetween":
            return "OccursInBetween";
        case "upThrough":
            return "InReportingLineOf";
    }
}

const CONDITION_BY_OPERATOR: Record<EmployeeFieldType, Partial<Record<RuleOperator, RuleCondition>>> = {
    option: { Equal: "is", NotEqual: "isNot", AnyOf: "isAnyOf", NoneOf: "isNoneOf" },
    date: {
        Equal: "isExactly",
        NotEqual: "isNot",
        LowerThan: "isBefore",
        GreaterThan: "isAfter",
        Between: "isBetween",
        OccurredMoreThan: "isOlderThan",
        OccurredLessThan: "isWithinTheLast",
        OccurredBetween: "occurredBetween",
        OccursInMoreThan: "occursInMoreThan",
        OccursInLessThan: "occursInLessThan",
        OccursInBetween: "occursInBetween"
    },
    number: { Equal: "is", NotEqual: "isNot", GreaterThan: "isGreaterThan", LowerThan: "isLessThan", Between: "isBetween" },
    person: { Equal: "directlyTo", AnyOf: "directlyTo", InReportingLineOf: "upThrough" },
    personReference: { Equal: "is", NotEqual: "isNot", AnyOf: "isAnyOf", NoneOf: "isNoneOf" }
};

export function toRuleCondition(operator: RuleOperator, fieldType: EmployeeFieldType) {
    return CONDITION_BY_OPERATOR[fieldType][operator];
}

export interface RuleDraft {
    key: string;
    fieldId?: string;
    condition?: RuleCondition;
    values: string[];
    periodType?: PeriodType;
    hasUnsupportedCondition?: boolean;
}

export type RuleDraftError = "incomplete" | "missingRangeEnd";

export function validateRuleDraft(draft: RuleDraft): RuleDraftError | undefined {
    if (!draft.fieldId || !draft.condition) {
        return "incomplete";
    }

    const arity = valueArity(draft.condition);

    if (arity === "period") {
        const count = parseRuleNumber(draft.values[0] ?? "");

        return !draft.periodType || Number.isNaN(count) || count <= 0 ? "incomplete" : undefined;
    }

    const filled = draft.values.filter(value => value.trim());

    switch (arity) {
        case "range":
        case "periodRange":
            if (filled.length < 2) {
                return "missingRangeEnd";
            }

            return arity === "periodRange" && !draft.periodType ? "incomplete" : undefined;
        default:
            return filled.length > 0 ? undefined : "incomplete";
    }
}

function isRuleValueParsable(value: string, fieldType: EmployeeFieldType, condition: RuleCondition): boolean {
    if (!value.trim()) {
        return true;
    }

    const arity = valueArity(condition);

    if (arity === "period" || arity === "periodRange") {
        return !Number.isNaN(parseRuleNumber(value));
    }

    if (fieldType === "date") {
        return parseRuleDate(value) !== null;
    }

    if (fieldType === "number") {
        return !Number.isNaN(parseRuleNumber(value));
    }

    return true;
}

export function toRuleDrafts(rules: SegmentRule[], fieldTypeById: Map<string, EmployeeFieldType>): RuleDraft[] {
    return rules.map(rule => {
        const fieldType = fieldTypeById.get(rule.propertyId);
        const condition = fieldType ? toRuleCondition(rule.operator, fieldType) : undefined;

        return {
            key: crypto.randomUUID(),
            fieldId: rule.propertyId,
            condition,
            values: condition ? rule.values.map(value => (fieldType && isRuleValueParsable(value, fieldType, condition) ? value : "")) : [],
            periodType: condition && (valueArity(condition) === "period" || valueArity(condition) === "periodRange") ? rule.periodType : undefined,
            hasUnsupportedCondition: !!fieldType && !condition
        };
    });
}

export function toSegmentRules(drafts: RuleDraft[]): SegmentRule[] {
    return drafts
        .map(draft => {
            if (!draft.fieldId || !draft.condition || validateRuleDraft(draft)) {
                return undefined;
            }

            const values = draft.values.filter(value => value.trim());

            return { propertyId: draft.fieldId, operator: toRuleOperator(draft.condition, values.length), values, ...(draft.periodType && { periodType: draft.periodType }) };
        })
        .filter((rule): rule is SegmentRule => !!rule);
}
